import React from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Sparkles, ShieldX } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const location = useLocation();
  const accessDenied = location.state?.accessDenied;
  const accessDeniedMsg = location.state?.message || "Access denied. You have not been added as a member of this workspace.";

  const handleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex-1 flex flex-col justify-between p-8 md:p-14">
        <div className="flex items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_three-zinc/artifacts/h16r009z_Three%20ZInc%20logo.png"
            alt="Three Zinc"
            className="h-10 w-auto select-none"
            data-testid="brand-logo"
          />
        </div>

        <div className="max-w-md w-full mx-auto md:mx-0 md:ml-8 flex flex-col gap-8">
          <div className="flex flex-col gap-3">
            <span className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-blue-700 bg-sky-50 px-3 py-1 rounded-full">
              <Sparkles className="h-3 w-3" /> Workspace
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
              World's 1<sup className="text-3xl sm:text-4xl">st</sup> <span className="text-brand">Unified Orchestrated</span> AI Marketing Platform
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              <span className="font-medium text-slate-800">Single Vision:</span> Buy back time for every marketing individual.
            </p>
          </div>

          <button
            onClick={handleSignIn}
            data-testid="google-login-button"
            className="btn-brand group w-full h-12 rounded-xl font-medium flex items-center justify-center gap-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
              <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
              <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
              <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
            </svg>
            Continue with Google
            <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          {accessDenied && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex gap-3" data-testid="access-denied-banner">
              <ShieldX className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-rose-700">Access denied</p>
                <p className="text-xs text-rose-600">{accessDeniedMsg}</p>
                <p className="text-xs text-slate-600">Ask the workspace owner to add your email under Users → Members.</p>
              </div>
            </div>
          )}

          <p className="text-xs text-slate-500">
            By continuing you agree to our Terms and Privacy policy.
          </p>
        </div>

        <p className="text-xs text-slate-400">© 2026 Three Zinc. All rights reserved.</p>
      </div>

      {/* Right: Visual */}
      <div className="hidden lg:flex flex-1 p-6">
        <div
          className="relative flex-1 rounded-3xl overflow-hidden bg-gradient-to-br from-mint-50 via-sky-50 to-rose-50"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1767473907132-9e52351d023a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNvZnQlMjBwYXN0ZWwlMjAzZHxlbnwwfHx8fDE3Nzc2MzM0NDd8MA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/60">
            <p className="font-display text-lg font-medium text-slate-900 leading-snug">
              "Three Zinc replaced four tools for us — it feels calm, looks gorgeous, and just works."
            </p>
            <p className="mt-3 text-sm text-slate-600">— Ines R., Head of Product</p>
          </div>
        </div>
      </div>
    </div>
  );
}
